'use client'
import { useCallback, useEffect, useState } from 'react'
import CommunityLoading from 'component/block/loading/community-loading'
import ScrollLoad from 'component/block/scrollLoad'
import { Portal } from 'component/ui/portal'
import { HEADER_LOCATION_CONTAINER_ID } from 'constant'
import { useLocationPickerMenuTitles } from 'hook/useLocationPickerMenuTitles'
import useScrollDetector from 'hook/useScrollDetector'
import { useCommunityLocationPickerStore } from 'store/global/location-picker'
import { usePickerOpenStore } from 'store/global/picker-open'
import { useIsMobile } from 'store/server-context/device'
import CommunityWithComments from './community-with-comments'
import LocationPicker from './locationPicker'
import useCommunityFeed from './use-community-feed'
import { Button } from 'component/ui/button'

import IconAdd from 'asset/icon/general/add.svg'
import cn from 'util/cn'
import { usePopupStore } from 'store/global/popup'

const CommunityFeed = () => {
  const [currentTitle, setCurrentTitle] = useState<{
    index: number
    title: string
  } | null>(null)

  const pickedLocation = useCommunityLocationPickerStore(
    state => state.pickedLocation
  )
  const setPickerOpen = usePickerOpenStore(state => state.setPickerOpen)
  const showDownloadPopup = usePopupStore(state => state.showDownloadPopup)
  const isMobile = useIsMobile()

  const {
    data: communities,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading
  } = useCommunityFeed(pickedLocation)

  const menuTitles = useLocationPickerMenuTitles(pickedLocation, communities)

  const { isScrolling } = useScrollDetector()

  const handleCurrentChange = useCallback(
    (index: number) => {
      const community = communities[index]
      if (!community) {
        return
      }
      setCurrentTitle({
        index,
        title: community.location?.display_name
      })

      const geoPoint = community.loc
      useCommunityLocationPickerStore.setState({
        currentGeo: community.loc
          ? {
              lat: geoPoint.lat,
              lng: geoPoint.lng
            }
          : null
      })
    },
    [communities]
  )

  useEffect(() => {
    if (isScrolling) {
      setPickerOpen(false)
    }
  }, [isScrolling, setPickerOpen])

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const element = entry.target as HTMLElement
            const index = Number(element.getAttribute('data-question-index'))
            handleCurrentChange(index)
          }
        })
      },
      {
        threshold: 0,
        rootMargin: '0% 0% -95% 0%'
      }
    )

    Array.from(document.querySelectorAll('[data-location-index]')).forEach(
      node => {
        observer.observe(node)
      }
    )

    return () => {
      observer.disconnect()
    }
  }, [handleCurrentChange])
  return (
    <div className='relative mx-auto min-h-[inherit] w-full max-w-[--max-content-size] px-[--content-padding]'>
      <Portal
        getNode={() =>
          isMobile
            ? document.getElementById(HEADER_LOCATION_CONTAINER_ID)
            : null
        }
      >
        <LocationPicker menuTitles={menuTitles} currentTitle={currentTitle} />
      </Portal>
      {communities.map((community, index) => {
        return (
          <CommunityWithComments
            key={`${community.id}_${community.asset_id}_${index}`}
            comment={community.comments}
            isCommunityPageLink
          />
        )
      })}

      <ScrollLoad
        callback={fetchNextPage}
        fetching={isFetching}
        fetchEnd={!isLoading && !hasNextPage}
        loader={<CommunityLoading />}
      />

      <div
        className={cn(
          'bottom-5 flex justify-end',
          communities.length < 6
            ? 'absolute w-[calc(100%_-_var(--content-padding)_*_2)] '
            : 'sticky w-full'
        )}
      >
        <Button
          $icon={true}
          className='size-[56px] mr-2'
          $radius='round'
          onClick={() => {
            showDownloadPopup()
          }}
        >
          <IconAdd width={24} height={24} />
        </Button>
      </div>
    </div>
  )
}

export default CommunityFeed
